import React, { useState } from "react";
import { formatActivityTimestamp, getActivityIcon, getCategoryLabel, getCategoryColor } from "./helpers";

// Activity history: notes, contact logs, quarterly reports, edits (revertable),
// reverts, and checklist changes — newest first, filterable by kind.
const ActivityHistoryList = ({ activityLog, revertingEntry, onRevert }) => {
    const [activityFilter, setActivityFilter] = useState("all");

    return (
        <div className="activity-list">
            <div className="activity-list-header">
                <h4>Activity History</h4>
                <div className="activity-filter-tabs">
                    {[
                        { value: "all", label: "All" },
                        { value: "general", label: "General" },
                        { value: "contact", label: "Contact Log" },
                        { value: "quarterly", label: "Quarterly" },
                        { value: "checklist", label: "Checklist" },
                        { value: "edit", label: "Edits" },
                    ].map(f => (
                        <button
                            key={f.value}
                            className={`filter-tab ${activityFilter === f.value ? 'active' : ''}`}
                            onClick={() => setActivityFilter(f.value)}
                        >{f.label}</button>
                    ))}
                </div>
            </div>
            {(!activityLog || activityLog.length === 0) ? (
                <p className="no-activity">No activity yet. Edits and notes will appear here.</p>
            ) : (
                <div className="activity-entries">
                    {[...(activityLog || [])]
                        .sort((a, b) => {
                            const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                            const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                            return dateB - dateA;
                        })
                        .filter(entry => {
                            if (activityFilter === "all") return true;
                            if (activityFilter === "edit") return entry.type === "edit" || entry.type === "revert";
                            if (activityFilter === "checklist") return entry.type === "checklist";
                            return entry.category === activityFilter;
                        })
                        .map((entry) => (
                            <div key={entry.id} className={`activity-entry activity-${entry.type} ${entry.category ? `activity-cat-${entry.category}` : ''}`}>
                                <div className="activity-content">
                                    <div className="activity-main-line">
                                        <span className="activity-icon">{getActivityIcon(entry.type, entry.category)}</span>
                                        <span className="activity-admin">{entry.adminEmail || "Unknown user"}</span>
                                        {entry.type === "edit" && (
                                            <span className="activity-action">edited <strong>{entry.fieldLabel}</strong></span>
                                        )}
                                        {entry.type === "note" && (
                                            <span className="activity-action">
                                                {entry.category === "contact" ? "logged contact" :
                                                 entry.category === "quarterly" ? "submitted check-in" :
                                                 "added a note"}
                                            </span>
                                        )}
                                        {entry.type === "revert" && (
                                            <span className="activity-action">reverted <strong>{entry.fieldLabel}</strong></span>
                                        )}
                                        {entry.type === "checklist" && (
                                            <span className="activity-action">
                                                {entry.itemKey === "finalized"
                                                    ? (entry.checked ? <>finalized the <strong>Onboarding Checklist</strong></> : <>reopened the <strong>Onboarding Checklist</strong></>)
                                                    : (entry.checked ? <>checked off <strong>{entry.itemLabel}</strong></> : <>unchecked <strong>{entry.itemLabel}</strong></>)}
                                            </span>
                                        )}
                                        {entry.type === "note" && entry.category && (
                                            <span className="category-badge" style={{ background: getCategoryColor(entry.category) }}>
                                                {getCategoryLabel(entry.category)}
                                            </span>
                                        )}
                                        <span className="activity-time">{formatActivityTimestamp(entry.timestamp)}</span>
                                    </div>
                                    {entry.type === "edit" && (
                                        <div className="edit-values">
                                            <span className="old-value">{entry.oldValue}</span>
                                            <span className="arrow">→</span>
                                            <span className="new-value">{entry.newValue}</span>
                                        </div>
                                    )}
                                    {entry.type === "note" && entry.category !== "quarterly" && (
                                        <div className="activity-note">
                                            {entry.content}
                                        </div>
                                    )}
                                    {entry.type === "note" && entry.category === "quarterly" && entry.quarterlyData && (
                                        <div className="quarterly-report-display">
                                            <div className="quarterly-report-title">
                                                {entry.quarterlyData.quarter} {new Date(entry.timestamp?.toDate ? entry.timestamp.toDate() : entry.timestamp).getFullYear()} Check-In Report
                                            </div>
                                            <div className={`quarterly-rating-badge rating-${entry.quarterlyData.overallRating?.toLowerCase().replace(' ', '-')}`}>
                                                {entry.quarterlyData.overallRating}
                                            </div>
                                            {entry.quarterlyData.executiveSummary && (
                                                <p className="quarterly-summary">{entry.quarterlyData.executiveSummary}</p>
                                            )}
                                            <details className="quarterly-details">
                                                <summary>View Full Report</summary>
                                                <div className="quarterly-detail-grid">
                                                    {entry.quarterlyData.communicationRating && (<div><strong>Communication:</strong><p>{entry.quarterlyData.communicationRating}</p></div>)}
                                                    {entry.quarterlyData.schedulingLeadTimes && (<div><strong>Scheduling Lead Times:</strong><p>{entry.quarterlyData.schedulingLeadTimes}</p></div>)}
                                                    {entry.quarterlyData.referralConcerns && (<div><strong>Referral/Workflow Concerns:</strong><p>{entry.quarterlyData.referralConcerns}</p></div>)}
                                                    {entry.quarterlyData.patientComplianceConcerns && (<div><strong>Patient Compliance:</strong><p>{entry.quarterlyData.patientComplianceConcerns}</p></div>)}
                                                    {entry.quarterlyData.workingWell && (<div><strong>Working Well:</strong><p>{entry.quarterlyData.workingWell}</p></div>)}
                                                    {entry.quarterlyData.areasToImprove && (<div><strong>Areas to Improve:</strong><p>{entry.quarterlyData.areasToImprove}</p></div>)}
                                                    {entry.quarterlyData.supportNeeded && (<div><strong>Support Needed:</strong><p>{entry.quarterlyData.supportNeeded}</p></div>)}
                                                    {entry.quarterlyData.billingConcerns && (<div><strong>Billing/Records Concerns:</strong><p>{entry.quarterlyData.billingConcerns}</p></div>)}
                                                    {entry.quarterlyData.collaborationOpportunities && (<div><strong>Collaboration Opportunities:</strong><p>{entry.quarterlyData.collaborationOpportunities}</p></div>)}
                                                    {entry.quarterlyData.followUpItems && (<div><strong>Follow-Up Action Items:</strong><p>{entry.quarterlyData.followUpItems}</p></div>)}
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                    {entry.type === "revert" && (
                                        <div className="activity-revert-detail">
                                            back to "{entry.revertedTo}"
                                        </div>
                                    )}
                                </div>
                                {entry.type === "edit" && (
                                    <button
                                        className="revert-btn"
                                        onClick={() => onRevert(entry)}
                                        disabled={revertingEntry === entry.id}
                                        title="Revert this change"
                                    >
                                        {revertingEntry === entry.id ? "..." : "↩️"}
                                    </button>
                                )}
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default ActivityHistoryList;
